/**
 * Shared request-id + AbortController lifecycle primitive.
 *
 * This module is the OWNER of stale-request protection for the search-hook family (see the
 * README Maintainer Guide). Consumers:
 * - `searchListState/useSearchListState.ts` (single-instance)
 * - `useSearchFacetsState.ts` (single-instance)
 * - ticketed follow-up: the keyed per-listKey copies in `searchListState/useMultiSearchListState.ts`
 *   and `CoreSiteSearch/useSiteSearch.ts` (`useSiteSearchDetailState`)
 *
 * Designed as a factory so a keyed consumer can hold one instance per listKey
 * (`createKeyedRequestLifecycle`) with a dispose-all sweep mirroring the keyed consumers'
 * unmount cleanup.
 */

export type RequestLifecycleHandle = {
  controller: AbortController;
  requestId: number;
};

export type RequestLifecycle = {
  /**
   * Aborts any outstanding controller, bumps the request id (invalidating all previously
   * issued handles), and returns a fresh handle for the new request.
   */
  startRequest: () => RequestLifecycleHandle;
  /**
   * Whether a previously issued handle has been superseded (a newer request started) or
   * aborted. Resolvers must drop their result when this returns true.
   */
  isStale: (handle: RequestLifecycleHandle) => boolean;
  /** Aborts the outstanding controller (does not bump the request id). */
  abort: () => void;
};

export const createRequestLifecycle = (): RequestLifecycle => {
  let currentRequestId = 0;
  let currentController: AbortController | undefined;

  const abort = () => {
    currentController?.abort();
  };

  const startRequest = (): RequestLifecycleHandle => {
    currentRequestId += 1;

    abort();

    const controller = new AbortController();
    currentController = controller;

    return {
      controller,
      requestId: currentRequestId,
    };
  };

  const isStale = (handle: RequestLifecycleHandle) =>
    handle.controller.signal.aborted || handle.requestId !== currentRequestId;

  return {
    startRequest,
    isStale,
    abort,
  };
};

export type KeyedRequestLifecycle<Key extends string = string> = {
  /** Returns (lazily creating) the lifecycle instance for the given key. */
  get: (key: Key) => RequestLifecycle;
  /** Aborts every key's outstanding controller — the unmount sweep for keyed consumers. */
  abortAll: () => void;
};

export const createKeyedRequestLifecycle = <
  Key extends string = string,
>(): KeyedRequestLifecycle<Key> => {
  const lifecyclesByKey = new Map<Key, RequestLifecycle>();

  return {
    get: (key) => {
      const existing = lifecyclesByKey.get(key);

      if (existing) {
        return existing;
      }

      const created = createRequestLifecycle();
      lifecyclesByKey.set(key, created);
      return created;
    },
    abortAll: () => {
      lifecyclesByKey.forEach((lifecycle) => lifecycle.abort());
    },
  };
};
